import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { formatTime } from "@/utils/formatters";
import { RiskBadge as StatusBadge } from "@/components/RiskBadge";
import { GuestTags } from "@/components/GuestTags";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useSettings, useRiskLabels, useCustomRiskLevels } from "@/hooks/useValidations";
import { MagnifyingGlass, X, Car } from "@phosphor-icons/react";

export function ValidationsTab() {
  const [validations, setValidations] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("all");
  const [filters, setFilters] = useState({ room: "", car_plate: "", operator: "", location: "", start_date: "", end_date: "" });
  const [showFilters, setShowFilters] = useState(false);
  const settings = useSettings();
  const timezone = settings?.timezone || "UTC";
  const riskLabels = useRiskLabels(settings);
  const customLevels = useCustomRiskLevels(settings);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: "200", status: statusFilter });
      if (filters.room) params.set("room", filters.room);
      if (filters.car_plate) params.set("car_plate", filters.car_plate);
      if (filters.operator) params.set("operator", filters.operator);
      if (filters.location) params.set("location", filters.location);
      if (filters.start_date) params.set("start_date", new Date(filters.start_date).toISOString());
      if (filters.end_date) params.set("end_date", new Date(filters.end_date + "T23:59:59").toISOString());
      const { data } = await api.get(`/validations?${params.toString()}`);
      setValidations(data.validations);
      setTotal(data.total);
    } catch (err) {
      console.error(err);
    }
  }, [statusFilter, filters]);

  useEffect(() => { load(); }, [load]);

  const filterOptions = customLevels && customLevels.length > 0
    ? customLevels.map((lv) => ({ value: lv.key, label: lv.label }))
    : [{ value: "normal", label: "Normal" }, { value: "suspicious", label: "Suspicious" }, { value: "fraud", label: "Fraud" }];

  const hasActiveFilters = Object.values(filters).some(Boolean);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm text-[#A3A3A3]">{total} records</p>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)}
            className={`border-[#262626] rounded-sm text-xs ${showFilters ? "text-[#007AFF] border-[#007AFF]" : "text-[#A3A3A3]"}`} data-testid="toggle-filters-btn">
            <MagnifyingGlass size={14} className="mr-1" /> Filters {hasActiveFilters ? `(active)` : ""}
          </Button>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44 bg-[#0A0A0A] border-[#262626] text-white rounded-sm text-xs" data-testid="status-filter">
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-[#262626]">
              <SelectItem value="all">All Status</SelectItem>
              {filterOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {showFilters && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 bg-[#121212] border border-[#262626] rounded-sm p-3" data-testid="validation-filters">
          <Input placeholder="Room Number" value={filters.room} onChange={(e) => setFilters({ ...filters, room: e.target.value })}
            className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm text-xs h-8" data-testid="filter-room" />
          <Input placeholder="Car Plate" value={filters.car_plate} onChange={(e) => setFilters({ ...filters, car_plate: e.target.value })}
            className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm text-xs h-8" data-testid="filter-car-plate" />
          <Input placeholder="Operator" value={filters.operator} onChange={(e) => setFilters({ ...filters, operator: e.target.value })}
            className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm text-xs h-8" data-testid="filter-operator" />
          <Input placeholder="Location" value={filters.location} onChange={(e) => setFilters({ ...filters, location: e.target.value })}
            className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm text-xs h-8" data-testid="filter-location" />
          <Input type="date" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
            className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm text-xs h-8 font-mono-data" data-testid="filter-start-date" />
          <Input type="date" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
            className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm text-xs h-8 font-mono-data" data-testid="filter-end-date" />
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={() => setFilters({ room: "", car_plate: "", operator: "", location: "", start_date: "", end_date: "" })}
              className="text-[#FF3B30] hover:text-[#FF3B30] text-xs h-8" data-testid="clear-filters-btn">
              <X size={12} className="mr-1" /> Clear
            </Button>
          )}
        </div>
      )}

      <div className="overflow-auto max-h-[55vh]">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-[#262626] hover:bg-transparent">
              <TableHead className="text-xs uppercase tracking-[0.15em] text-[#737373]">Car Plate</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-[#737373]">Room</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-[#737373] text-center">Count</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-[#737373]">Operator</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-[#737373]">Location</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-[#737373]">Time</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-[#737373]">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {validations.map((v) => (
              <TableRow key={v.id} className={`border-b border-[#1A1A1A] hover:bg-[#1A1A1A] ${v.over_limit ? "bg-[#FF3B30]/5 border-l-2 border-l-[#FF3B30]" : ""}`}>
                <TableCell className="font-mono-data text-sm text-white tracking-wider">
                  {v.car_plate}
                  {Array.from({ length: Math.min(v.plate_edit_count || 0, 2) }).map((_, i) => (
                    <Car key={`edit-${i}`} size={12} weight="fill" className="inline ml-0.5 text-[#FF9F0A]" />
                  ))}
                </TableCell>
                <TableCell className="font-mono-data text-sm text-[#A3A3A3]">{v.room_number}<GuestTags vip={v.guest_vip} rateCode={v.guest_rate_code} /></TableCell>
                <TableCell className="font-mono-data text-sm text-[#007AFF] text-center">{v.room_validated_count ?? "-"}</TableCell>
                <TableCell className="text-sm text-[#A3A3A3] cursor-default uppercase" title={v.user_name}>{v.user_username || v.user_name}</TableCell>
                <TableCell className="text-sm text-[#A3A3A3]">{v.location}</TableCell>
                <TableCell className="font-mono-data text-xs text-[#737373]">{formatTime(v.timestamp, timezone)}</TableCell>
                <TableCell><StatusBadge status={v.risk_status} score={v.risk_score} labels={riskLabels} customLevels={customLevels} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
